import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import {
    DataGrid,
    GridColDef,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    useGridApiRef,
    GridRowParams,
    getGridStringOperators,
    getGridDateOperators,
} from '@mui/x-data-grid';
import { OnboardDeviceHardwareConfiguration, ToastType } from '../interfaces';
import { transformKeyToTitle } from '../utils/transformKeyToTitle';
import Button from '@mui/material/Button';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    loadOnboardDevicesHardwareConfigurations,
    selectOnboardDevicesHardwareConfigurations,
    selectSelectedOnboardHardwareConfigurationRows,
    setSelectedOnboardHardwareConfigurationRows,
} from '../store/onboardDeviceHardwareConfigurations.reducer';
import { UpsertOnboardDevicesHardwareConfigurationDialog } from './UpsertOnboardDevicesHardwareConfigurationDialog';
import { useManageGrid } from '../hooks/useManageGrid';
import { CopyTextCell } from './GridCells/CopyTextCell';
import { LinkCell } from './GridCells/LinkCell';
import { CameraConfigurationsCell } from './GridCells/CameraConfigurationsCell';
import { deleteOnboardDevicesHardwareConfiguration } from '../api/deleteOnboardDevicesHardwareConfiguration';
import { useToastMessage } from '../hooks/useToastMessage';
import ToastWrapper from './ToastWrapper';
import { CircularProgress } from '@mui/material';
import { formatDate } from '../utils/formatDate';

const HARDWARE_CONFIGURATION_COLUMN_DEFS = [
    { key: 'id', renderCell: CopyTextCell },
    { key: 'name', renderCell: CopyTextCell },
    { key: 'documentation', renderCell: LinkCell },
    {
        key: 'cameraConfigurations',
        headerName: 'Camera Configurations',
        renderCell: CameraConfigurationsCell,
        sortable: false,
        filterable: false,
    },
    { key: 'comment', renderCell: CopyTextCell },
    { key: 'createdBy', renderCell: CopyTextCell },
    { key: 'createdAt', valueFormatter: formatDate, filterOperators: getGridDateOperators },
    { key: 'updatedBy', renderCell: CopyTextCell },
    { key: 'updatedAt', valueFormatter: formatDate, filterOperators: getGridDateOperators },
];

interface DataGridComponentProps {
    configurations: OnboardDeviceHardwareConfiguration[];
}

const defaultOnboardDevicesHardwareConfiguration: OnboardDeviceHardwareConfiguration = {
    id: undefined,
    name: '',
    documentation: null,
    createdAt: '',
    createdBy: '',
    updatedAt: null,
    updatedBy: null,
    comment: null,
};

interface ToolbarProps {
    setIsOnboardDevicesHardwareConfigurationDialogOpen: (value: boolean) => void;
    setSelectedOnboardDevicesHardwareConfiguration: (
        value: React.SetStateAction<OnboardDeviceHardwareConfiguration>,
    ) => void;
}

function Toolbar({
    setIsOnboardDevicesHardwareConfigurationDialogOpen,
    setSelectedOnboardDevicesHardwareConfiguration,
}: ToolbarProps) {
    const dispatch = useAppDispatch();
    const selectedRows = useAppSelector(selectSelectedOnboardHardwareConfigurationRows);
    const configurations = useAppSelector(selectOnboardDevicesHardwareConfigurations);
    const { isOpened, toastMessage, setToastMessage, handleCloseToast } = useToastMessage();
    const [isDeleting, setIsDeleting] = useState(false);

    const handleAddClick = () => {
        setSelectedOnboardDevicesHardwareConfiguration(defaultOnboardDevicesHardwareConfiguration);
        setIsOnboardDevicesHardwareConfigurationDialogOpen(true);
    };

    const handleEditClick = () => {
        const selectedConfigId = selectedRows[0];
        const selectedConfiguration = configurations!.find((config) => config.id === selectedConfigId);

        setSelectedOnboardDevicesHardwareConfiguration(
            selectedConfiguration as OnboardDeviceHardwareConfiguration,
        );
        setIsOnboardDevicesHardwareConfigurationDialogOpen(true);
    };

    const handleDeleteClick = async () => {
        const confirmDelete = window.confirm('Ви дійсно хочете видалити? Це незворотня дія.');

        if (!confirmDelete) {
            return;
        }

        setIsDeleting(true);

        try {
            await deleteOnboardDevicesHardwareConfiguration(selectedRows[0]);
            onReload();
        } catch (error: any) {
            console.error(error);

            if (error.response && error.response.status === 400) {
                setToastMessage({
                    message: `Не вдалося видалити запис. На нього є посилання, видаліть спочатку їх. Детальніше: ${error.response.data.message}`,
                    type: ToastType.WARNING,
                });
            } else {
                setToastMessage({
                    message: `Не вдалося видалити запис. Помилка: ${error.message}`,
                    type: ToastType.WARNING,
                });
            }
        } finally {
            setIsDeleting(false);
        }
    };

    const onReload = () => {
        dispatch(loadOnboardDevicesHardwareConfigurations());
    };

    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <Button startIcon={<RefreshIcon />} color="primary" onClick={onReload}>
                Reload
            </Button>
            <Button startIcon={<AddIcon />} color="primary" onClick={handleAddClick}>
                Add
            </Button>
            <Button
                startIcon={<EditIcon />}
                color="primary"
                onClick={handleEditClick}
                disabled={!selectedRows || selectedRows.length !== 1}
            >
                Edit
            </Button>
            <Button
                startIcon={<DeleteIcon />}
                color="primary"
                onClick={handleDeleteClick}
                disabled={!selectedRows || selectedRows.length !== 1 || isDeleting}
            >
                Delete
                {isDeleting && <CircularProgress style={{ marginLeft: '5px' }} size={15} />}
            </Button>

            <ToastWrapper
                open={isOpened}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={handleCloseToast}
                message={toastMessage?.message}
                type={toastMessage?.type}
            />
        </GridToolbarContainer>
    );
}

export const OnboardDevicesHardwareConfigurationsTableGrid = ({ configurations }: DataGridComponentProps) => {
    const dispatch = useAppDispatch();
    const [
        upsertOnboardDevicesHardwareConfigurationDialogOpen,
        setUpsertOnboardDevicesHardwareConfigurationDialogOpen,
    ] = useState(false);
    const [initialValues, setInitialValues] = useState(defaultOnboardDevicesHardwareConfiguration);
    const {
        sortModel,
        columnsConfig,
        columnsWidth,
        gridFilterModel,
        handleColumnVisibilityChange,
        handleColumnWidthChange,
        handleSortModelChange,
        handleFilterModelChange,
        onCellKeyDown,
        gridStateSynced,
    } = useManageGrid('onboard-device-hardware-configurations');
    const apiRef = useGridApiRef();

    const selectedRows = useAppSelector(selectSelectedOnboardHardwareConfigurationRows);

    const onRowSelectionModelChange = (rows: any) => {
        dispatch(setSelectedOnboardHardwareConfigurationRows(rows as string[]));
    };

    const onRowDoubleClick = (params: GridRowParams) => {
        const id = params.id;
        dispatch(setSelectedOnboardHardwareConfigurationRows([id as string]));

        apiRef.current.selectRow(params.id, true, true);

        const selectedConfiguration = configurations!.find((config) => config.id === id);

        setInitialValues(selectedConfiguration as OnboardDeviceHardwareConfiguration);
        setUpsertOnboardDevicesHardwareConfigurationDialogOpen(true);
    };

    const columns: GridColDef[] = useMemo(() => {
        return HARDWARE_CONFIGURATION_COLUMN_DEFS.map((col) => ({
            field: col.key,
            headerName: col.headerName || transformKeyToTitle(col.key),
            renderCell: col.renderCell,
            valueFormatter: col.valueFormatter,
            width: columnsWidth[col.key] || 250,
            align: 'left' as const,
            headerAlign: 'left' as const,
            sortable: col.sortable !== false,
            filterable: col.filterable !== false,
            filterOperators:
                col.filterable === false
                    ? []
                    : col.filterOperators
                    ? col.filterOperators()
                    : getGridStringOperators(),
        }));
    }, [columnsWidth]);

    const rows = useMemo(() => {
        if (!configurations) {
            return;
        }

        return configurations.map((configuration) => {
            const row: any = {
                ...configuration,
            };

            if (row.updatedAt) row.updatedAt = new Date(row.updatedAt);
            if (row.createdAt) row.createdAt = new Date(row.createdAt);

            return row;
        });
    }, [configurations]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {gridStateSynced && (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGrid
                        sx={{ height: '100%' }}
                        apiRef={apiRef}
                        rows={rows}
                        columns={columns}
                        slots={{
                            toolbar: (props) => (
                                <Toolbar
                                    {...props}
                                    setIsOnboardDevicesHardwareConfigurationDialogOpen={
                                        setUpsertOnboardDevicesHardwareConfigurationDialogOpen
                                    }
                                    setSelectedOnboardDevicesHardwareConfiguration={setInitialValues}
                                />
                            ),
                        }}
                        disableRowSelectionOnClick
                        disableMultipleRowSelection
                        sortModel={sortModel}
                        filterModel={gridFilterModel}
                        onSortModelChange={handleSortModelChange}
                        onColumnWidthChange={handleColumnWidthChange}
                        onColumnVisibilityModelChange={handleColumnVisibilityChange}
                        onFilterModelChange={handleFilterModelChange}
                        columnVisibilityModel={columnsConfig}
                        onCellKeyDown={onCellKeyDown}
                        onRowSelectionModelChange={onRowSelectionModelChange}
                        rowSelectionModel={selectedRows}
                        checkboxSelection
                        onRowDoubleClick={onRowDoubleClick}
                    />
                </Box>
            )}

            {upsertOnboardDevicesHardwareConfigurationDialogOpen && (
                <UpsertOnboardDevicesHardwareConfigurationDialog
                    open={upsertOnboardDevicesHardwareConfigurationDialogOpen}
                    onClose={() => setUpsertOnboardDevicesHardwareConfigurationDialogOpen(false)}
                    configuration={initialValues}
                />
            )}
        </Box>
    );
};
